import { AxiosError } from 'axios';
import client from './client';

export type ConfidenceBandDistribution = {
  none: number;
  zero: number;
  low: number;
  medium: number;
  high: number;
};

export type ClusterSuggestionListItem = {
  id: string;
  suggested_label: string;
  item_count: number;
  status: string;
  created_at: string;
  confidence_cue: string;
  confidence_band_distribution: ConfidenceBandDistribution;
};

export type ClusterSuggestionListResponse = {
  clusters: ClusterSuggestionListItem[];
  total: number;
  skipped_count?: number;
};

export type ClusterPreviewExampleItem = {
  work_item_id: string;
  title: string;
  classification_source: string;
  confidence_band: string;
  evidence_summary?: string | null;
};

export type ClusterOverrideImpact = {
  excluded_count: number;
  statement: string;
};

export type ClusterPreviewResponse = {
  id: string;
  suggested_label: string;
  item_count: number;
  status: string;
  created_at: string;
  confidence_cue: string;
  confidence_band_distribution: ConfidenceBandDistribution;
  grouping_rationale_summary: string;
  override_impact: ClusterOverrideImpact;
  example_items: ClusterPreviewExampleItem[];
};

export type ClusterGenerationJob = {
  id: string;
  status: string;
  progress_stage?: string | null;
  metrics?: Record<string, unknown>;
  error_class?: string | null;
  failure_reason?: string | null;
  requested_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  trigger_source?: string;
};

export type ClusterJobCreateResponse = {
  job_id: string;
  status: string;
  poll_url: string;
  replay?: boolean;
};

export type ClusterGenerationJobContext = {
  active_job: ClusterGenerationJob | null;
  last_terminal_job: ClusterGenerationJob | null;
};

export type OrganizeGeneralApiErrorDetail = {
  message?: string;
  error_class?: string;
  next_step?: string;
  active_job_id?: string;
};

export function parseOrganizeGeneralApiError(err: unknown, fallback: string): string {
  const detail =
    err instanceof AxiosError
      ? (err.response?.data as { detail?: string | OrganizeGeneralApiErrorDetail } | undefined)?.detail
      : undefined;
  if (typeof detail === 'string') {
    return detail;
  }
  if (detail && typeof detail === 'object') {
    const parts = [detail.message ? String(detail.message) : fallback];
    if (detail.next_step) {
      parts.push(String(detail.next_step));
    }
    return parts.join(' ');
  }
  return fallback;
}

export function getOrganizeGeneralErrorClass(err: unknown): string | null {
  if (!(err instanceof AxiosError)) {
    return null;
  }
  const detail = (err.response?.data as { detail?: OrganizeGeneralApiErrorDetail } | undefined)?.detail;
  if (detail && typeof detail === 'object' && detail.error_class) {
    return String(detail.error_class);
  }
  return null;
}

export function getOrganizeGeneralActiveJobId(err: unknown): string | null {
  if (!(err instanceof AxiosError)) {
    return null;
  }
  const detail = (err.response?.data as { detail?: OrganizeGeneralApiErrorDetail } | undefined)?.detail;
  if (detail && typeof detail === 'object' && detail.active_job_id) {
    return String(detail.active_job_id);
  }
  return null;
}

export async function fetchClusterSuggestions(
  status = 'pending_review',
): Promise<ClusterSuggestionListResponse> {
  const res = await client.get<ClusterSuggestionListResponse>('/organize-general/cluster-suggestions', {
    params: { status },
  });
  return res.data;
}

export async function fetchClusterPreview(clusterId: string): Promise<ClusterPreviewResponse> {
  const res = await client.get<ClusterPreviewResponse>(
    `/organize-general/cluster-suggestions/${encodeURIComponent(clusterId)}`,
  );
  return res.data;
}

export async function fetchClusterJobContext(): Promise<ClusterGenerationJobContext> {
  const res = await client.get<ClusterGenerationJobContext>('/organize-general/cluster-jobs/context');
  return res.data;
}

export async function fetchClusterJob(jobId: string): Promise<ClusterGenerationJob> {
  const res = await client.get<ClusterGenerationJob>(`/organize-general/cluster-jobs/${jobId}`);
  return res.data;
}

export async function startClusterGenerationJob(
  idempotencyKey?: string,
): Promise<ClusterJobCreateResponse> {
  const res = await client.post<ClusterJobCreateResponse>('/organize-general/cluster-jobs', {
    idempotency_key: idempotencyKey,
  });
  return res.data;
}

export type ClusterApproveResponse = {
  cluster_id: string;
  status: string;
  suggested_label: string;
  item_count: number;
  decision_reason?: string | null;
  approved_at?: string | null;
};

export type ClusterApplyResponse = {
  cluster_id: string;
  status: string;
  lightweight_subdomain_id?: string | null;
  lightweight_subdomain_label?: string | null;
  applied_count: number;
  skipped_count: number;
  failed_count: number;
  undo_expires_at?: string | null;
  override_skipped_count: number;
};

export type ClusterUndoResponse = {
  cluster_id: string;
  status: string;
  restored_count: number;
  skipped_count: number;
  undo_expires_at?: string | null;
};

export async function approveCluster(
  clusterId: string,
  reason?: string,
): Promise<ClusterApproveResponse> {
  const res = await client.post<ClusterApproveResponse>(
    `/organize-general/cluster-suggestions/${encodeURIComponent(clusterId)}/approve`,
    reason ? { reason } : {},
  );
  return res.data;
}

export async function applyCluster(clusterId: string): Promise<ClusterApplyResponse> {
  const res = await client.post<ClusterApplyResponse>(
    `/organize-general/cluster-suggestions/${encodeURIComponent(clusterId)}/apply`,
  );
  return res.data;
}

export async function undoCluster(clusterId: string): Promise<ClusterUndoResponse> {
  const res = await client.post<ClusterUndoResponse>(
    `/organize-general/cluster-suggestions/${encodeURIComponent(clusterId)}/undo`,
  );
  return res.data;
}

export type ClusterDecisionResponse = {
  cluster_id: string;
  status: string;
  suggested_label: string;
  item_count: number;
  decision_reason?: string | null;
  rejected_at?: string | null;
  deferred_at?: string | null;
};

export async function rejectCluster(
  clusterId: string,
  reason?: string,
): Promise<ClusterDecisionResponse> {
  const res = await client.post<ClusterDecisionResponse>(
    `/organize-general/cluster-suggestions/${encodeURIComponent(clusterId)}/reject`,
    reason ? { reason } : {},
  );
  return res.data;
}

export async function deferCluster(
  clusterId: string,
  reason?: string,
): Promise<ClusterDecisionResponse> {
  const res = await client.post<ClusterDecisionResponse>(
    `/organize-general/cluster-suggestions/${encodeURIComponent(clusterId)}/defer`,
    reason ? { reason } : {},
  );
  return res.data;
}

/** Alias for deferCluster — product copy uses "Ignore for now". */
export const ignoreCluster = deferCluster;

export type LightweightSubdomainListItem = {
  id: string;
  display_label: string;
  work_item_count: number;
  created_at?: string | null;
};

export type LightweightSubdomainListResponse = {
  subdomains: LightweightSubdomainListItem[];
  total: number;
};

export type PromotionStructureSignal = {
  key: string;
  met: boolean;
  detail: string;
};

export type PromotionEligibilityResponse = {
  subdomain_id: string;
  display_label: string;
  eligible: boolean;
  reasons: string[];
  maturity: {
    work_item_count: number;
    min_work_items: number;
    age_days: number;
    min_age_days: number;
    approval_rate: number;
    min_approval_rate: number;
    undo_rejection_rate: number;
    max_undo_rejection_rate: number;
    structure_signals: PromotionStructureSignal[];
  };
  existing_suggestion_id?: string | null;
};

export type PromotionSuggestionCreateResponse = {
  id: string;
  status: string;
  source: string;
  expires_at: string;
  definition: Record<string, unknown>;
  evidence: Record<string, unknown>;
};

export async function fetchLightweightSubdomains(): Promise<LightweightSubdomainListResponse> {
  const res = await client.get<LightweightSubdomainListResponse>('/organize-general/lightweight-subdomains');
  return res.data;
}

export async function fetchPromotionEligibility(
  subdomainId: string,
): Promise<PromotionEligibilityResponse> {
  const res = await client.get<PromotionEligibilityResponse>(
    `/organize-general/lightweight-subdomains/${encodeURIComponent(subdomainId)}/promotion-eligibility`,
  );
  return res.data;
}

export async function createPromotionSuggestion(
  subdomainId: string,
): Promise<PromotionSuggestionCreateResponse> {
  const res = await client.post<PromotionSuggestionCreateResponse>(
    `/organize-general/lightweight-subdomains/${encodeURIComponent(subdomainId)}/promotion-suggestion`,
  );
  return res.data;
}

export function getPromotionErrorCode(err: unknown): string | null {
  if (!(err instanceof AxiosError)) {
    return null;
  }
  const detail = (err.response?.data as { detail?: { code?: string } } | undefined)?.detail;
  if (detail && typeof detail === 'object' && detail.code) {
    return String(detail.code);
  }
  return null;
}
